import React, { useContext, useMemo } from "react";

import SettingsContext from "~/contexts/SettingsContext";
import styles from "./AssetView.module.css";
import ThemeContext from "~/contexts/ThemeContext";
import Button, { ButtonTheme } from "~/components/Button/Button";
import { formatDateTime } from "~/helpers/dateHelper";
import { Asset } from "~/entities/authEntities";
import AuthContext from "~/contexts/AuthContext";
import { formatLoyaltyBalance } from "~/helpers/numberHelpers";
import OrderContext from "~/contexts/OrderContext";
import {
  LoyaltyDialogCardDescriptionFontSize,
  LoyaltyDialogCardTitleFontSize,
} from "~/theme/models/themeModels";
import pluralize from "pluralize";
import { capitalizeWords } from "~/helpers/stringHelpers";
import { uploadcareConfig } from "~/configs/uploadcareConfig";
import { isCurrentlyAvailable } from "~/helpers/availabilityHelpers";
import { toZonedTime } from "date-fns-tz";
import SiteContext from "~/contexts/SiteContext";
import AvailabilityView, {
  AvailabilityViewVariant,
} from "~/components/AvailabilityView/AvailabilityView";

type PromoModalProps = {
  asset: Asset;
  onSelect: () => void;
  selected?: boolean;
  onClose?: () => void;
};

const AssetView: React.FC<PromoModalProps> = ({
  asset,
  onSelect,
  selected,
  onClose,
}) => {
  const { orgSettings } = useContext(SettingsContext);
  const { template } = useContext(ThemeContext);
  const { authenticatedUser } = useContext(AuthContext);
  const { readonlyCart, orderSummary } = useContext(OrderContext);
  const { site } = useContext(SiteContext);

  const requestedTime = orderSummary.requestedReadyTime;
  const available = useMemo(() => {
    if (asset.availability == null || asset.availabilityUnknown) {
      return true;
    }

    const localTime = toZonedTime(
      requestedTime ? new Date(requestedTime) : new Date(),
      site.timeZone
    );
    return isCurrentlyAvailable(asset.availability, localTime);
  }, [
    requestedTime,
    asset.availability,
    site.timeZone,
    asset.availabilityUnknown,
  ]);

  const canAfford = (asset: Asset) => {
    return (
      asset?.price && (authenticatedUser?.loyaltyBalance ?? 0) > asset.price
    );
  };

  const getButtonText = () => {
    if (asset.price) {
      return canAfford(asset)
        ? `Add to my ${pluralize.plural(promoName.toLowerCase())}`
        : "Unavailable";
    }

    return selected
      ? `${capitalizeWords(pluralize.singular(promoName))} applied`
      : available
      ? `Use ${capitalizeWords(pluralize.singular(promoName))}`
      : "Unavailable";
  };

  // Styles
  // Card
  const cardBackgroundColor = template.loyaltyDialog.card.backgroundColour;
  const cardBorderColor = template.loyaltyDialog.card.borderColour;
  const cardTitleFontSize =
    LoyaltyDialogCardTitleFontSize[template.loyaltyDialog.card.titleFontSize];
  const cardTitleFontColor = template.loyaltyDialog.card.titleFontColour;
  const cardDescriptionFontSize =
    LoyaltyDialogCardDescriptionFontSize[
      template.loyaltyDialog.card.descriptionFontSize
    ];
  const cardDescriptionFontColor =
    template.loyaltyDialog.card.descriptionFontColour;

  const cardButtonThemePrimary: ButtonTheme = {
    color: template.loyaltyDialog.card.buttonColour,
    borderColor: template.loyaltyDialog.card.buttonBorderColour,
    fontColor: template.loyaltyDialog.card.buttonFontColour,
    hoverColor: template.loyaltyDialog.card.buttonHoverColour,
    hoverBorderColor: template.loyaltyDialog.card.buttonHoverBorderColour,
    hoverFontColor: template.loyaltyDialog.card.buttonHoverFontColour,
    size: template.loyaltyDialog.card.buttonSize,
    style: template.loyaltyDialog.card.buttonStyle,
  };
  const cardButtonThemeSecondary: ButtonTheme = {
    color: template.global.button.secondButtonColour,
    borderColor: template.global.button.secondButtonBorderColour,
    fontColor: template.global.button.secondButtonFontColour,
    hoverColor: template.global.button.hoverSecondButtonColour,
    hoverBorderColor: template.global.button.hoverSecondButtonBorderColour,
    hoverFontColor: template.global.button.hoverSecondButtonFontColour,
    size: template.loyaltyDialog.card.buttonSize,
    style: template.loyaltyDialog.card.buttonStyle,
  };

  const promoName = orgSettings?.loyaltySettings?.promotionName ?? "Promotion";

  return (
    <div
      className={styles.content}
      style={{
        backgroundColor: cardBackgroundColor,
        borderColor: cardBorderColor,
      }}
    >
      {asset.image != null && (
        <img
          src={asset?.image}
          className={styles.img}
          alt={asset?.name ?? ""}
        />
      )}
      {asset.imageRef != null && (
        <img
          className={styles.img}
          src={uploadcareConfig.getUrl(
            asset.imageRef.fileId,
            asset.imageRef.modifiers
          )}
          alt={asset.imageRef.fileName}
        />
      )}
      <div className={styles.cardContent}>
        <span
          className={styles.title}
          style={{
            fontSize: cardTitleFontSize,
            color: cardTitleFontColor,
          }}
        >
          {asset?.name}
        </span>

        {onClose ? (
          <div className={styles.buttonsWrapper}>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={readonlyCart}
              theme={cardButtonThemeSecondary}
            >
              Use later
            </Button>
            <Button
              variant="primary"
              onClick={onSelect}
              disabled={readonlyCart || !available}
              theme={cardButtonThemePrimary}
            >
              Use now
            </Button>
          </div>
        ) : (
          <div className={styles.buttonsWrapper}>
            <Button
              variant="secondary"
              onClick={onSelect}
              disabled={
                !!(selected || (asset.price && !canAfford(asset))) ||
                readonlyCart ||
                !available
              }
              theme={cardButtonThemePrimary}
            >
              {getButtonText()}
            </Button>
          </div>
        )}

        <div
          className={styles.description}
          style={{
            fontSize: cardDescriptionFontSize,
            color: cardDescriptionFontColor,
          }}
        >
          <span>
            {asset.price
              ? `${formatLoyaltyBalance(orgSettings, asset.price)} ${
                  orgSettings.loyaltySettings?.balanceUnitName
                } - ${asset.description}`
              : asset.description}
          </span>
          {asset?.validUntil && (
            <span>Expires {formatDateTime(new Date(asset?.validUntil))}</span>
          )}
          {!!asset?.availability?.length && (
            <AvailabilityView
              availability={asset?.availability}
              variant={AvailabilityViewVariant.Short}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetView;
